import { Button, Stack, useMediaQuery, useTheme } from '@mui/material'
import dayjs from 'dayjs'
import { useFormik } from 'formik'
import { isUndefined } from 'lodash'
import { FC, ReactNode, useEffect } from 'react'
import { useCreateReplyScheduleApiV1ReplyScheduleThreadThreadIdPost } from '../../api/gen/base'
import { ModalDialogWrapper } from '../../components/ModalDialogWrapper'
import { Notistack } from '../../components/Notistack'
import { RegularRequestForm } from '../../components/RegularRequestForm'
import {
    RegularRequestFormFile,
    regularRequestFormValidationSchema,
    RegularRequestFormValues,
} from '../../components/RegularRequestForm/RegularRequestForm'
import { useWrapperStore } from '../../store/storeWrapper'
import { ThreadDTO } from '../../swagger/dto'
import {
    getRegularRequestFormStartDate,
    splitRegularRequestFormFiles,
} from '../../utils/regularRequests'

export type RegularRequestCreateDialogData = {
    threadId?: number
    text: string
    files?: RegularRequestFormFile[]
    startDate?: Date
}

type Props = {
    open: boolean
    initialData: RegularRequestCreateDialogData | null
    onClose: VoidFunction
    handleCreateThread?: (name: string) => Promise<ThreadDTO | undefined>
    onSubmit?: VoidFunction
    additionalContent?: ReactNode
}

export const RegularRequestCreateDialog: FC<Props> = ({
    open,
    initialData,
    onClose,
    handleCreateThread,
    onSubmit,
    additionalContent,
}) => {
    const { breakpoints } = useTheme()
    const isMobile = useMediaQuery(breakpoints.down('md'))

    const { currentAgentNameInDialog, modelDetail, uploadFiles, isLoadingUploadFiles } =
        useWrapperStore((state) => state)

    const { isSuccess, isError, mutate, isPending, reset } =
        useCreateReplyScheduleApiV1ReplyScheduleThreadThreadIdPost()

    const initialValues: RegularRequestFormValues = {
        files: initialData?.files ?? [],
        text: initialData?.text ?? '',
        requestSchedule: [
            {
                repeatType: 'ONCE',
                startDateTime: dayjs(
                    getRegularRequestFormStartDate(initialData?.startDate ?? new Date()),
                ),
            },
        ],
    }

    const formik = useFormik<RegularRequestFormValues>({
        initialValues,
        enableReinitialize: true,
        validationSchema: regularRequestFormValidationSchema,
        validateOnMount: true,
        onSubmit: async (formData) => {
            if (!initialData) {
                return
            }

            const { requestSchedule, text, files } = formData
            const requestData = requestSchedule[0]

            let threadId = initialData.threadId

            if (isUndefined(threadId)) {
                const threadData = await handleCreateThread?.(
                    text.length > 200 ? text.substring(0, 197) + '...' : text,
                )

                threadId = threadData?.id ?? undefined
            }

            if (isUndefined(threadId)) {
                return
            }

            const { fileUrls, newFiles } = splitRegularRequestFormFiles(files)

            const newFilesUrl = await uploadFiles({ files: newFiles }, threadId)

            mutate({
                thread_id: threadId,
                params: {
                    min_balance: Number(!modelDetail?.is_free),
                },
                data: {
                    send_time: requestData.startDateTime.toISOString(),
                    repeat: requestData.repeatType,
                    weekdays: requestData.repeatDays,
                    contexts: [],
                    text,
                    agent: currentAgentNameInDialog,
                    files: [...fileUrls, ...newFilesUrl],
                },
            })
        },
    })

    useEffect(() => {
        if (isSuccess) {
            onSubmit?.()
            onClose?.()
        }
    }, [isSuccess])

    useEffect(() => {
        if (open) {
            formik.setValues(initialValues)
        }
    }, [open])

    const isLoading = isPending || isLoadingUploadFiles

    return (
        <>
            <ModalDialogWrapper
                open={open}
                handleClose={onClose}
                title="Добавить в регулярные запросы"
                sx={{
                    '.MuiPaper-root': {
                        width: '647px',
                        maxWidth: '647px',
                        height: 'auto',
                    },

                    '.MuiDialogActions-root': {
                        borderTop: '1px solid #F4F4F6',
                    },

                    [breakpoints.down('md')]: {
                        '.MuiPaper-root': {
                            m: 0,
                            borderRadius: 0,
                            height: '100%',
                            maxHeight: 'unset',
                        },
                    },
                }}
                footerActions={
                    <>
                        <Stack direction="row" gap={2} width="100%" justifyContent="flex-end">
                            <Button variant="outlined" onClick={onClose} fullWidth={isMobile}>
                                Отмена
                            </Button>
                            <Button
                                fullWidth={isMobile}
                                variant="contained"
                                onClick={() => formik.handleSubmit()}
                                disabled={!formik.isValid || isLoading}>
                                Добавить
                            </Button>
                        </Stack>
                    </>
                }>
                {additionalContent}
                <RegularRequestForm formik={formik} disabled={isLoading} />
            </ModalDialogWrapper>
            <Notistack
                isError={isError}
                open={isError || isSuccess}
                message={
                    isError ? 'Ошибка создания регулярного запроса' : 'Регулярный запрос создан'
                }
                onClose={() => reset()}
            />
        </>
    )
}
